import { ChangeEvent, useEffect, useState } from "react";
import {
  Box,
  ButtonBase,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { ClientTypes } from "../../features/auth/types/authType";
import { Company } from "../../features/setups/types/setupTypes";
import { selectAuth } from "../../features/auth/authSlice";
import { useTranslation } from "react-i18next";
import { ChevronRight, Close, FilterList } from "@mui/icons-material";
import {
  EmployeeStatus,
  CompletionStatus,
  Nationalities,
  ExperiencePeriods,
  AgeGap,
  LanguageExperienceFilter,
  ProfessionalTypesFilter,
  ReligionTypesFilter,
  MaritalStatusFilter,
  LanguageExperience,
  MaritalStatus,
  MedicalStatus,
  ProfessionalTypes,
  ReligionTypes,
} from "../../features/process/types/processTypes";
import { CompanyFilter } from "./CompanyFilter";
import {
  selectPreference,
  setAgeEnd,
  setAgeStart,
  setArabic,
  setAmharic,
  setBiometricsCompleted,
  setCocCompleted,
  setEnglish,
  setExperienceYears,
  setLocalAgency,
  setMaritalStatus,
  setMedicalStatus,
  setAddressRegion,
  setBrokerName,
  setNationality,
  setProfession,
  setReligion,
  setStatus,
  toggleThis,
} from "../../features/preferences/preferencesSlice";
import { RegionFilterAutocomplete } from "../../features/process/components/RegionFilterAutocomplete";
import { BrokerNameFilterAutocomplete } from "../../features/process/components/BrokerNameFilterAutocomplete";

interface Props {
  listType: string;
}
export const ListFilter = ({ listType }: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");

  const { user, loading } = useAppSelector(selectAuth);

  const {
    agencyId,
    status,
    nationality,
    addressRegion,
    brokerName,
    maritalStatus,
    religion,
    profession,
    arabic,
    amharic,
    english,
    ageStart,
    ageEnd,
    experienceYears,
    cocCompleted,
    medicalStatus,
    biometricsCompleted,
  } = useAppSelector(selectPreference);

  const [localCompany, setLocalCompany] = useState<Company>();

  const onChangeStatus = (value: ChangeEvent<HTMLInputElement>) => {
    const val = value.target.value as EmployeeStatus;
    dispatch(setStatus(val === EmployeeStatus.all ? undefined : val));
  };

  const onChangeNationality = (value: ChangeEvent<HTMLInputElement>) => {
    const val = value.target.value as Nationalities;
    dispatch(setNationality(val === Nationalities.all ? undefined : val));
  };
  const onChangeMaritalStatus = (value: ChangeEvent<HTMLInputElement>) => {
    const val = value.target.value as MaritalStatus;
    dispatch(
      setMaritalStatus(val === ("all" as MaritalStatus) ? undefined : val),
    );
  };
  const onChangeReligion = (value: ChangeEvent<HTMLInputElement>) => {
    const val = value.target.value as ReligionTypes;
    dispatch(setReligion(val === ("all" as ReligionTypes) ? undefined : val));
  };
  const onChangeProfession = (value: ChangeEvent<HTMLInputElement>) => {
    const val = value.target.value as ProfessionalTypes;
    dispatch(
      setProfession(val === ("all" as ProfessionalTypes) ? undefined : val),
    );
  };
  const onChangeArabic = (value: ChangeEvent<HTMLInputElement>) => {
    const val = value.target.value as LanguageExperience;
    dispatch(
      setArabic(val === ("all" as LanguageExperience) ? undefined : val),
    );
  };
  const onChangeAmharic = (value: ChangeEvent<HTMLInputElement>) => {
    const val = value.target.value as LanguageExperience;
    dispatch(
      setAmharic(val === ("all" as LanguageExperience) ? undefined : val),
    );
  };
  const onChangeEnglish = (value: ChangeEvent<HTMLInputElement>) => {
    const val = value.target.value as LanguageExperience;
    dispatch(
      setEnglish(val === ("all" as LanguageExperience) ? undefined : val),
    );
  };
  const onChangeAgeStart = (value: ChangeEvent<HTMLInputElement>) => {
    if (value.target.value === "all") dispatch(setAgeStart(undefined));
    else dispatch(setAgeStart(parseInt(value.target.value)));
  };
  const onChangeAgeEnd = (value: ChangeEvent<HTMLInputElement>) => {
    if (value.target.value === "all") dispatch(setAgeEnd(undefined));
    else dispatch(setAgeEnd(parseInt(value.target.value)));
  };
  const onChangeExperienceYears = (value: ChangeEvent<HTMLInputElement>) => {
    if (value.target.value === "all") dispatch(setExperienceYears(undefined));
    else dispatch(setExperienceYears(parseInt(value.target.value)));
  };

  const onChangeCoc = (value: ChangeEvent<HTMLInputElement>) => {
    const val = value.target.value as CompletionStatus;
    dispatch(setCocCompleted(val === CompletionStatus.all ? undefined : val));
  };

  const onChangeMedical = (value: ChangeEvent<HTMLInputElement>) => {
    const val = value.target.value;
    dispatch(
      setMedicalStatus(
        val === "all" ? undefined : (val as MedicalStatus),
      ),
    );
  };

  const onChangeBiometrics = (value: ChangeEvent<HTMLInputElement>) => {
    const val = value.target.value as CompletionStatus;
    dispatch(
      setBiometricsCompleted(val === CompletionStatus.all ? undefined : val),
    );
  };

  // Skip while undefined (CompanyFilter not resolved yet), or mounting the
  // sidebar would wipe a previously applied agency/agent filter.
  useEffect(() => {
    if (!localCompany) return;
    dispatch(
      setLocalAgency(localCompany._id !== "-1" ? localCompany._id : undefined),
    );
  }, [dispatch, localCompany]);

  const handleDrawerClose = () => {
    dispatch(toggleThis({ type: "Filter", newValue: false }));
  };
  return (
    <Box sx={{ pt: 2, backgroundColor: "primary.main" }}>
      {loading === "pending" ? (
        <LinearProgress color="secondary" />
      ) : (
        <Stack sx={{ backgroundColor: "white" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ backgroundColor: "primary.main", color: "white" }}
          >
            <Stack direction="row" alignItems="center" justifyContent="start">
              <FilterList />
              <Typography variant="h6" component="div">
                {t("filter")}
              </Typography>
            </Stack>
            <IconButton
              style={{ color: "white" }}
              onClick={handleDrawerClose}
              size="large"
            >
              <Close />
            </IconButton>
          </Stack>
          <Grid
            container
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{ px: 1, pb: 5, pt: 1 }}
          >
            {/* Foreign-office filtering now lives inline above each list
                (OfficeFilter); Moderators pick their agency from the navbar
                AgencySwitcher. Only a ForeignAgent user keeps an in-drawer
                agency (LocalAgency) picker here. */}
            {user?.company?.type === ClientTypes.ForeignAgent && (
              <Grid item xs={12}>
                <CompanyFilter
                  companyType={ClientTypes.LocalAgency}
                  setCompany={setLocalCompany}
                  includeAll={true}
                  companyId={agencyId}
                />
              </Grid>
            )}

            {listType === "employees" && (
              <Grid item xs={12}>
                <TextField
                  id="statuses"
                  select
                  label={t("status")}
                  onChange={onChangeStatus}
                  value={status ?? EmployeeStatus.all}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ mt: 0 }}
                  SelectProps={{
                    sx: {
                      ".MuiSvgIcon-root": {
                        color: "white",
                      },
                    },
                  }}
                >
                  {Object.keys(EmployeeStatus).map((option) => (
                    <MenuItem key={option} value={option as string}>
                      {te(option.toLowerCase())}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {listType === "employees" ? (
              <>
                <Grid item xs={12}>
                  <RegionFilterAutocomplete
                    value={addressRegion}
                    onChange={(next) => dispatch(setAddressRegion(next))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <BrokerNameFilterAutocomplete
                    value={brokerName}
                    onChange={(next) => dispatch(setBrokerName(next))}
                  />
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <TextField
                  id="nationalities"
                  select
                  label={te("nationality")}
                  onChange={onChangeNationality}
                  value={nationality ?? Nationalities.all}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ mt: 0 }}
                  SelectProps={{
                    sx: {
                      ".MuiSvgIcon-root": {
                        color: "white",
                      },
                    },
                  }}
                >
                  {Object.keys(Nationalities).map((option) => (
                    <MenuItem key={option} value={option as string}>
                      {te(option.toLowerCase())}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                id="maritalStatus"
                select
                label={te("maritalStatus")}
                onChange={onChangeMaritalStatus}
                value={maritalStatus ?? MaritalStatusFilter.all}
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mt: 0 }}
                SelectProps={{
                  sx: {
                    ".MuiSvgIcon-root": {
                      color: "white",
                    },
                  },
                }}
              >
                {Object.keys(MaritalStatusFilter).map((option) => (
                  <MenuItem key={option} value={option as string}>
                    {te(option.toLowerCase())}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="religion"
                select
                label={te("religion")}
                onChange={onChangeReligion}
                value={religion ?? ReligionTypesFilter.all}
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mt: 0 }}
                SelectProps={{
                  sx: {
                    ".MuiSvgIcon-root": {
                      color: "white",
                    },
                  },
                }}
              >
                {Object.keys(ReligionTypesFilter).map((option) => (
                  <MenuItem key={option} value={option as string}>
                    {te(option.toLowerCase())}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="profession"
                select
                label={te("profession")}
                onChange={onChangeProfession}
                value={profession ?? ProfessionalTypesFilter.all}
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mt: 0 }}
                SelectProps={{
                  sx: {
                    ".MuiSvgIcon-root": {
                      color: "white",
                    },
                  },
                }}
              >
                {Object.keys(ProfessionalTypesFilter).map((option) => (
                  <MenuItem key={option} value={option as string}>
                    {te(option.toLowerCase())}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={4}>
              <TextField
                id="arabic"
                select
                label={te("arabic")}
                onChange={onChangeArabic}
                value={arabic ?? LanguageExperienceFilter.all}
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mt: 0 }}
                SelectProps={{
                  sx: {
                    ".MuiSvgIcon-root": {
                      color: "white",
                    },
                  },
                }}
              >
                {Object.keys(LanguageExperienceFilter).map((option) => (
                  <MenuItem key={option} value={option as string}>
                    {te(option.toLowerCase())}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={4}>
              <TextField
                id="english"
                select
                label={te("english")}
                onChange={onChangeEnglish}
                value={english ?? LanguageExperienceFilter.all}
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mt: 0 }}
                SelectProps={{
                  sx: {
                    ".MuiSvgIcon-root": {
                      color: "white",
                    },
                  },
                }}
              >
                {Object.keys(LanguageExperienceFilter).map((option) => (
                  <MenuItem key={option} value={option as string}>
                    {te(option.toLowerCase())}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={4}>
              <TextField
                id="amharic"
                select
                label={te("amharic")}
                onChange={onChangeAmharic}
                value={amharic ?? LanguageExperienceFilter.all}
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mt: 0 }}
                SelectProps={{
                  sx: {
                    ".MuiSvgIcon-root": {
                      color: "white",
                    },
                  },
                }}
              >
                {Object.keys(LanguageExperienceFilter).map((option) => (
                  <MenuItem key={option} value={option as string}>
                    {te(option.toLowerCase())}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="experienceYears"
                select
                label={t("workExperience")}
                onChange={onChangeExperienceYears}
                value={
                  experienceYears !== undefined
                    ? String(experienceYears)
                    : ExperiencePeriods.all
                }
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mt: 0 }}
                SelectProps={{
                  sx: {
                    ".MuiSvgIcon-root": {
                      color: "white",
                    },
                  },
                }}
              >
                {Object.values(ExperiencePeriods).map((option) => (
                  <MenuItem key={option} value={option as string}>
                    {te(option.toLowerCase())}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                id="ageStart"
                select
                label={te("fromAge")}
                onChange={onChangeAgeStart}
                value={ageStart !== undefined ? String(ageStart) : AgeGap.all}
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mt: 0 }}
                SelectProps={{
                  sx: {
                    ".MuiSvgIcon-root": {
                      color: "white",
                    },
                  },
                }}
              >
                {Object.values(AgeGap).map((option) => (
                  <MenuItem key={option} value={option as string}>
                    {te(option.toLowerCase())}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                id="ageEnd"
                select
                label={te("toAge")}
                onChange={onChangeAgeEnd}
                value={ageEnd !== undefined ? String(ageEnd) : AgeGap.all}
                variant="outlined"
                size="small"
                fullWidth
                sx={{ mt: 0 }}
                SelectProps={{
                  sx: {
                    ".MuiSvgIcon-root": {
                      color: "white",
                    },
                  },
                }}
              >
                {Object.values(AgeGap).map((option) => (
                  <MenuItem key={option} value={option as string}>
                    {te(option.toLowerCase())}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Candidates view omits COC, medical, and biometrics filters. */}
            {listType === "employees" && (
              <>
                <Grid item xs={12}>
                  <TextField
                    id="coc"
                    select
                    label={te("coc")}
                    onChange={onChangeCoc}
                    value={cocCompleted ?? CompletionStatus.all}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{ mt: 0 }}
                    SelectProps={{
                      sx: {
                        ".MuiSvgIcon-root": {
                          color: "white",
                        },
                      },
                    }}
                  >
                    {Object.keys(CompletionStatus).map((option) => (
                      <MenuItem key={option} value={option as string}>
                        {te(option.toLowerCase())}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="medical"
                    select
                    label={te("medical")}
                    onChange={onChangeMedical}
                    value={medicalStatus ?? "all"}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{ mt: 0 }}
                    SelectProps={{
                      sx: {
                        ".MuiSvgIcon-root": {
                          color: "white",
                        },
                      },
                    }}
                  >
                    {["all", ...Object.keys(MedicalStatus)].map((option) => (
                      <MenuItem key={option} value={option as string}>
                        {te(option.toLowerCase())}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    id="biometrics"
                    select
                    label={te("biometrics")}
                    onChange={onChangeBiometrics}
                    value={biometricsCompleted ?? CompletionStatus.all}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{ mt: 0 }}
                    SelectProps={{
                      sx: {
                        ".MuiSvgIcon-root": {
                          color: "white",
                        },
                      },
                    }}
                  >
                    {Object.keys(CompletionStatus).map((option) => (
                      <MenuItem key={option} value={option as string}>
                        {te(option.toLowerCase())}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}
          </Grid>
        </Stack>
      )}
    </Box>
  );
};
